import React, { useState, useEffect } from 'react';
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import SlidePageWrapper from './SlidePageWrapper';
import { depositService } from '../services/depositService';
import { withdrawService } from '../services/withdrawService';
import { useTranslation } from '../i18n/LocaleContext';

const TransactionHistoryModal = ({ isOpen, onClose }) => {
  const { t, language } = useTranslation();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTransactions();
    }
  }, [isOpen]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      
      // Fetch deposits và withdraws
      const [depositsData, withdrawsData] = await Promise.all([
        depositService.getHistory(50),
        withdrawService.getHistory(50)
      ]);

      // Merge và format data
      const deposits = depositsData.deposits.map(d => ({
        id: `deposit-${d.id}`,
        type: 'deposit',
        amount: parseFloat(d.amount),
        status: d.status,
        date: new Date(d.created_at).toLocaleString(language === 'zh' ? 'zh-CN' : language === 'ko' ? 'ko-KR' : language === 'ja' ? 'ja-JP' : language === 'en' ? 'en-US' : 'vi-VN'),
        note: d.note || t('deposits'),
        created_at: d.created_at
      }));

      const withdraws = withdrawsData.withdraws.map(w => ({
        id: `withdraw-${w.id}`,
        type: 'withdraw',
        amount: parseFloat(w.amount),
        status: w.status,
        date: new Date(w.created_at).toLocaleString(language === 'zh' ? 'zh-CN' : language === 'ko' ? 'ko-KR' : language === 'ja' ? 'ja-JP' : language === 'en' ? 'en-US' : 'vi-VN'),
        note: w.bank_name ? `${t('withdrawTo')} ${w.bank_name}` : t('withdrawals'),
        created_at: w.created_at
      }));

      // Merge và sort theo thời gian mới nhất
      const allTransactions = [...deposits, ...withdraws].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );

      setTransactions(allTransactions);
    } catch (error) {
      console.error('Fetch transactions error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const value = amount || 0;
    const isWholeNumber = value % 1 === 0;
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: isWholeNumber ? 0 : 2,
      maximumFractionDigits: isWholeNumber ? 0 : 2
    }).format(value);
    return `$${formatted}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': 
        return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">{t('transactionSuccess')}</span>;
      case 'pending': 
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">{t('pending')}</span>;
      case 'rejected': 
        return <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">{t('transactionRejected')}</span>;
      default: 
        return null;
    }
  };

  return (
    <SlidePageWrapper isOpen={isOpen} onClose={onClose} title={t('depositWithdrawHistory')}>
        <div className="bg-gray-50">
          {loading ? (
            <div className="p-8 text-center">
              <p className="text-gray-600">{t('loading')}</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-600">{t('noTransactions')}</p>
            </div>
          ) : (
            <div className="p-4 space-y-3 pb-24">
              {transactions.map((tx) => (
              <div key={tx.id} className="bg-white rounded-xl border border-black/[0.05] overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      tx.type === 'deposit' ? 'bg-green-50' : 'bg-red-50'
                    }`}>
                      {tx.type === 'deposit' ? (
                        <ArrowDownCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <ArrowUpCircle className="w-6 h-6 text-red-500" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1 min-w-0 pr-2">
                          <h3 className="font-semibold text-gray-900 text-base">
                            {tx.type === 'deposit' ? t('deposits') : t('withdrawals')}
                          </h3>
                          <p className="text-sm text-gray-500 mt-0.5">{tx.date}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`text-lg font-bold ${
                            tx.type === 'deposit' ? 'text-green-600' : 'text-red-500'
                          }`}>
                            {tx.type === 'deposit' ? '' : '-'}{formatCurrency(tx.amount)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <p className="text-sm text-gray-600">{tx.note}</p>
                        {getStatusBadge(tx.status)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              ))}
            </div>
          )}
        </div>
    </SlidePageWrapper>
  );
};

export default TransactionHistoryModal;
